import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query'; //-tech-debt-react-query;
import { ReactSortable } from 'react-sortablejs';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import { Add, ArrowBack, ViewQuilt, Visibility } from '@mui/icons-material';
import AdminConfirmDialog from '../Common/AdminConfirmDialog';
import { fetchItems } from '../../../support/queries';
import { CacheKey } from '../../../types/cache-keys';
import { DEFAULT_QUERY_OPTIONS } from '../../../support/constants';
import {
  useAssessmentMutations,
  useFetchAssessment,
  useFetchSections,
  useReorder,
} from '../../../hooks/assessment-hooks';
import {
  arrayMove,
  AssessmentQuestion,
  AssessmentSection,
  byOrder,
  uniqueKey,
} from '../../../support/assessments';
import AssessmentSectionCard from './AssessmentSectionCard';
import AssessmentMetaHeader from './AssessmentMetaHeader';
import AssessmentPreviewDialog from './AssessmentPreviewDialog';

/**
 * The builder for one assessment: its metadata and its section → question tree.
 *
 * Fetches the assessment, its sections, and each section's questions (one query per section, the
 * same shape the fill-in surface uses), then renders a drag-to-reorder list of section cards. It is
 * the single owner of the loaded questions, so every question is available as a `visible_when`
 * source and a write anywhere in the tree invalidates and re-flows here. Editing is entirely inline
 * — sections and questions are created immediately with auto-assigned keys and edited in place, and
 * order is set by dragging (there is no key or order field anymore).
 */

type Props = {
  assessmentId: number;
  onBack: () => void;
};

const AssessmentBuilder: React.FC<Props> = ({ assessmentId, onBack }) => {
  const { data: assessment, isLoading: loadingAssessment } = useFetchAssessment(assessmentId);
  const { data: sectionsRaw = [], isLoading: loadingSections } = useFetchSections(assessmentId);
  const sections = useMemo(() => byOrder(sectionsRaw), [sectionsRaw]);

  const questionQueries = useQueries({
    queries: sections.map((section) => ({
      queryKey: [String(CacheKey.AssessmentQuestion), String(section.id)],
      queryFn: () => fetchItems('db/assessment-question', { section: section.id }),
      enabled: !!section.id,
      ...DEFAULT_QUERY_OPTIONS,
    })),
  });

  const questionsBySection = useMemo(() => {
    const map: Record<number, AssessmentQuestion[]> = {};
    sections.forEach((section, index) => {
      map[section.id] = byOrder((questionQueries[index]?.data as AssessmentQuestion[]) || []);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, questionQueries.map((q) => q.dataUpdatedAt).join(',')]);

  const allQuestions = useMemo(
    () => Object.values(questionsBySection).flat(),
    [questionsBySection]
  );

  const sectionMutations = useAssessmentMutations('section');
  const reorderSections = useReorder('section');
  const navigate = useNavigate();

  const [previewOpen, setPreviewOpen] = useState(false);

  // Local drag order for sections, re-seeded whenever the fetched sections change in content (not
  // just position). Keying only on `id:order` meant a field edit (active flag, conditional rule)
  // refetched with the same id/order left the signature unchanged, so the row snapped back to the
  // stale value; a content-based signature reflects the edit while still leaving an identical
  // signature — and therefore an in-flight drag — untouched.
  const [items, setItems] = useState<AssessmentSection[]>(sections);
  const signature = useMemo(() => JSON.stringify(sections), [sections]);
  useEffect(() => {
    setItems(sections);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const [pendingDeleteSection, setPendingDeleteSection] = useState<AssessmentSection | null>(null);

  const handleAddSection = () =>
    sectionMutations.create.mutate({
      assessment: assessmentId,
      key: uniqueKey('section', sections.map((s) => s.key)),
      title: 'New Section',
      description: '',
      order: items.length,
      visible_when: null,
      active: true,
    });

  const handleSectionDrop = (evt: { oldIndex?: number; newIndex?: number }) => {
    const { oldIndex, newIndex } = evt;
    if (oldIndex == null || newIndex == null || oldIndex === newIndex) return;
    const next = arrayMove(items, oldIndex, newIndex);
    setItems(next);
    reorderSections.mutate(next);
  };

  const handleDeleteSection = async () => {
    if (!pendingDeleteSection) return;
    await sectionMutations.remove.mutateAsync(pendingDeleteSection.id);
    setPendingDeleteSection(null);
  };

  if (loadingAssessment) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (!assessment) {
    return (
      <Box p={2}>
        <Button startIcon={<ArrowBack />} onClick={onBack}>
          Back
        </Button>
        <Typography sx={{ mt: 2 }}>Assessment not found.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>
        All Assessments
      </Button>

      <AssessmentMetaHeader assessment={assessment} />

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1">Sections</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ViewQuilt />}
            onClick={() => navigate(`/admin/assessments/${assessment.id}/report`)}
          >
            Edit report layout
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Visibility />}
            onClick={() => setPreviewOpen(true)}
            disabled={allQuestions.length === 0}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={handleAddSection}
            disabled={sectionMutations.create.isPending}
          >
            Add Section
          </Button>
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {loadingSections ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No sections yet. Add one to start building the assessment.
        </Typography>
      ) : (
        <ReactSortable
          list={items}
          setList={setItems}
          handle=".section-drag-handle"
          animation={150}
          onEnd={handleSectionDrop}
        >
          {items.map((section) => (
            <AssessmentSectionCard
              key={section.id}
              section={section}
              questions={questionsBySection[section.id] || []}
              allQuestions={allQuestions}
              onDeleteSection={() => setPendingDeleteSection(section)}
            />
          ))}
        </ReactSortable>
      )}

      <AssessmentPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        assessment={assessment}
        sections={sections}
        questionsBySection={questionsBySection}
        allQuestions={allQuestions}
      />

      <AdminConfirmDialog
        open={!!pendingDeleteSection}
        title="Delete Section"
        message={
          <Box>
            <Typography variant="body1" gutterBottom>
              {`Delete "${pendingDeleteSection?.title || 'this section'}"?`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This also deletes every question in it, and cannot be undone.
            </Typography>
          </Box>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="error"
        loading={sectionMutations.remove.isPending}
        onConfirm={handleDeleteSection}
        onCancel={() => setPendingDeleteSection(null)}
      />
    </Box>
  );
};

export default AssessmentBuilder;
